import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Loader, Send, Phone, Shield } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES_LIST = [
  'Individual Tax Return (1040)',
  'Business Return (LLC/S-Corp/C-Corp)',
  'Self-Employed / 1099',
  'Multi-State Filing',
  'Tax Planning & Consulting',
  'IRS Audit Representation',
  'Back Tax Filing',
  'Startup Tax Setup',
  'Amendment (1040-X)',
  'Other',
];

const SERVICES_LIST_ES = [
  'Declaración Individual (1040)',
  'Declaración Comercial (LLC/S-Corp/C-Corp)',
  'Autónomo / 1099',
  'Declaración Multi-Estado',
  'Planificación y Consultoría Fiscal',
  'Representación de Auditoría del IRS',
  'Declaración de Impuestos Atrasados',
  'Configuración Fiscal de Startup',
  'Enmienda (1040-X)',
  'Otro',
];

const GetQuotePage = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', service_interest: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const services = lang === 'es' ? SERVICES_LIST_ES : SERVICES_LIST;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/leads`, { ...form, source: 'quote' });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || (lang === 'es' ? 'Error al enviar. Intente de nuevo.' : 'Failed to submit. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const pricingPoints = lang === 'es' ? [
    'Sin cargos ocultos — cotización firme antes de comenzar',
    'Tarifa fija según la complejidad de su situación',
    'Precio comunicado y acordado por adelantado',
    'Facturación transparente línea por línea',
  ] : [
    'No hidden fees — firm quote before we start',
    'Flat-fee based on your situation\'s complexity',
    'Fee disclosed and agreed upon in advance',
    'Transparent line-item invoicing',
  ];

  return (
    <div style={{ paddingTop: '72px' }}>
      <section className="py-14 px-6" style={{ background: '#0B1F3A' }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="label-uppercase mb-3" style={{ color: '#D4AF37' }}>
            {lang === 'es' ? 'Cotización' : 'Quote'}
          </div>
          <h1 className="font-heading text-4xl font-extrabold text-white tracking-tight mb-3">
            {lang === 'es' ? 'Obtenga una Cotización Personalizada' : 'Get a Custom Quote'}
          </h1>
          <p className="text-blue-200 text-lg">
            {lang === 'es' 
              ? 'Precios transparentes — sin sorpresas. Cuéntenos sus necesidades y le proporcionaremos una cotización firme.'
              : 'Transparent pricing — no surprises. Tell us about your needs and we\'ll provide a firm quote.'
            }
          </p>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Left - Why Us */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">
                {lang === 'es' ? 'Nuestra Promesa de Precios' : 'Our Pricing Promise'}
              </h2>
              <p className="text-[#475569] text-sm mb-6">
                {lang === 'es' 
                  ? 'Creemos en la transparencia total. Nunca se sorprenderá con nuestra factura.'
                  : 'We believe in complete transparency. You\'ll never be surprised by our invoice.'
                }
              </p>
              
              <div className="space-y-4 mb-8">
                {pricingPoints.map((point) => (
                  <div key={point} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-gray-100">
                    <CheckCircle size={18} className="text-[#10B981] flex-shrink-0 mt-0.5" />
                    <span className="text-[#0B1F3A] text-sm font-medium">{point}</span>
                  </div>
                ))}
              </div>

              <div className="p-6 rounded-xl" style={{ background: '#0B1F3A' }}>
                <div className="flex items-center gap-3 mb-4">
                  <Shield size={24} style={{ color: '#D4AF37' }} />
                  <div className="text-white font-semibold">
                    {lang === 'es' ? '¿Prefiere Hablar?' : 'Prefer to Talk?'}
                  </div>
                </div>
                <p className="text-blue-200 text-sm mb-4">
                  {lang === 'es' 
                    ? 'Llámenos para una consulta gratuita y le daremos una cotización por teléfono.'
                    : 'Call us for a free consultation and we\'ll give you a quote over the phone.'
                  }
                </p>
                <a href="tel:7702356999" className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold text-sm hover:underline">
                  <Phone size={16} /> 770-235-6999
                </a>
              </div>
            </div>

            {/* Right - Quote Form */}
            <div className="lg:col-span-3">
              {success ? (
                <div className="bg-white rounded-2xl p-12 shadow-card border border-gray-100 text-center" data-testid="quote-success">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#10B98120' }}>
                    <CheckCircle size={32} style={{ color: '#10B981' }} />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-3">
                    {lang === 'es' ? '¡Solicitud de Cotización Recibida!' : 'Quote Request Received!'}
                  </h3>
                  <p className="text-[#475569] mb-8">
                    {lang === 'es' 
                      ? 'Prepararemos una cotización personalizada y nos comunicaremos dentro de 24 horas hábiles.'
                      : 'We\'ll prepare a custom quote and reach out within 24 business hours.'
                    }
                  </p>
                  <div className="flex gap-3 justify-center">
                    <Link to="/book-appointment">
                      <button className="btn-primary px-6 py-3 text-sm">
                        {lang === 'es' ? 'Reservar Consulta' : 'Book Consultation'}
                      </button>
                    </Link>
                    <Link to="/">
                      <button className="px-6 py-3 rounded-full border-2 border-[#0B1F3A] text-[#0B1F3A] font-semibold text-sm">
                        {lang === 'es' ? 'Volver al Inicio' : 'Back to Home'}
                      </button>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 shadow-card border border-gray-100">
                  <h2 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">
                    {lang === 'es' ? 'Solicite Su Cotización' : 'Request Your Quote'}
                  </h2>
                  <p className="text-[#475569] text-sm mb-7">
                    {lang === 'es' 
                      ? 'Cuéntenos sus necesidades y responderemos dentro de 24 horas.'
                      : 'Tell us about your needs and we\'ll respond within 24 hours.'
                    }
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">
                          {t('fullName')} *
                        </label>
                        <input 
                          value={form.name} 
                          onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} 
                          required 
                          placeholder={lang === 'es' ? 'Juan García' : 'John Smith'} 
                          className="w-full h-12 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" 
                          data-testid="quote-name-input" 
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">
                          {t('phone')}
                        </label>
                        <input 
                          value={form.phone} 
                          onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} 
                          placeholder="(770) 000-0000" 
                          className="w-full h-12 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" 
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">
                        {t('email')} *
                      </label>
                      <input 
                        type="email" 
                        value={form.email} 
                        onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} 
                        required 
                        placeholder={lang === 'es' ? 'juan@ejemplo.com' : 'john@example.com'} 
                        className="w-full h-12 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" 
                        data-testid="quote-email-input" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">
                        {lang === 'es' ? 'Servicio Principal' : 'Primary Service'}
                      </label>
                      <select 
                        value={form.service_interest} 
                        onChange={(e) => setForm(f => ({ ...f, service_interest: e.target.value }))} 
                        className="w-full h-12 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" 
                        data-testid="quote-service-select"
                      >
                        <option value="">{t('selectOption')}</option>
                        {services.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">
                        {lang === 'es' ? 'Cuéntenos Más' : 'Tell Us More'}
                      </label>
                      <textarea 
                        value={form.message} 
                        onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} 
                        rows={4} 
                        placeholder={lang === 'es' 
                          ? 'Describa su situación fiscal, número de estados, tipo de negocio, circunstancias especiales...'
                          : 'Describe your tax situation, number of states, business type, special circumstances...'
                        } 
                        className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] resize-none transition-all" 
                        data-testid="quote-message-input" 
                      />
                    </div>
                    {error && <div className="text-red-500 text-sm">{error}</div>}
                    <button 
                      type="submit" 
                      disabled={loading} 
                      className="w-full h-12 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-lg disabled:opacity-60" 
                      style={{ background: '#0B1F3A' }} 
                      data-testid="quote-submit-btn"
                    >
                      {loading 
                        ? <><Loader size={16} className="animate-spin" /> {lang === 'es' ? 'Enviando...' : 'Submitting...'}</> 
                        : <><Send size={16} /> {lang === 'es' ? 'Solicitar Cotización' : 'Request Quote'}</>
                      }
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetQuotePage;
